import React, { useEffect, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import {
  Box,
  TextField,
  InputAdornment,
  Card,
  Typography,
} from "@mui/material";
import Paginations from "../../common/pagination/Paginations";
import { allVendorProductsApi, deleteProductApi, venderProductDetailApi } from "../../api";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ReyLeaf from "../../common/assests/reyleafLogo.svg";
import { toast } from "react-toastify";
import { useConfirm } from "../../common/confirmDialog/useConfirm";
import { AdminTable, ActionCell } from "../../common/adminTable";
import configuration from "../../config/Config";
import { formatMoney } from "../../utils/configUtils";
import StatusChip from "../../common/statusChip/StatusChip";
import ProductDetailModal from "../../common/productDetailModal/ProductDetailModal";

const StarRating = ({ rating = 0 }) => {
  const full = Math.floor(rating);
  const half = rating - full > 0;
  return (
    <Box sx={{ display: "flex" }}>
      {Array.from({ length: full }, (_, i) => (
        <StarIcon key={i} sx={{ color: "#FFD700", fontSize: 18 }} />
      ))}
      {half && <StarHalfIcon sx={{ color: "#FFD700", fontSize: 18 }} />}
      {Array.from({ length: 5 - full - (half ? 1 : 0) }, (_, i) => (
        <StarOutlineIcon key={`e${i}`} sx={{ color: "#FFD700", fontSize: 18 }} />
      ))}
    </Box>
  );
};

const AllProductList = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [allVenderProducts, setAllVenderProducts] = useState([]);
  const [productDetailsData, setProductDetailsData] = useState({});
  const [myTimout, setMyTimeOut] = useState();
  const [change, setChange] = useState(false);

  const handleClose = () => setOpen(false);

  const handleAllProducts = async () => {
    try {
      setLoading(true);
      const response = await allVendorProductsApi({ token, currentPage, id, searchQuery });
      setAllVenderProducts(response?.products || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  const handleProductDetails = async (productId) => {
    try {
      const response = await venderProductDetailApi({ token, id: productId });
      setProductDetailsData(response || {});
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    const isConfirmed = await confirm({
      title: "Delete Product",
      description: "Are you sure you want to delete this product?",
      confirmText: "Delete",
      destructive: true,
    });
    if (!isConfirmed) return;
    try {
      const response = await deleteProductApi({ token, id: productId });
      setChange(!change);
      toast.success(response.message);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleClickOpen = (row) => {
    setSelectedRow(row);
    handleProductDetails(row?._id);
    setOpen(true);
  };

  useEffect(() => {
    handleAllProducts();
  }, [currentPage, change]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      handleAllProducts();
    } else {
      clearTimeout(myTimout);
      const timeout = setTimeout(handleAllProducts, 500);
      setMyTimeOut(timeout);
    }
  }, [searchQuery]);

  const columns = [
    {
      field: "photo",
      headerName: "Image",
      width: 70,
      renderCell: (row) => (
        <img
          src={row?.photos?.length > 0 ? `${configuration.cdnFront}/${row.photos[0].url}` : ReyLeaf}
          alt="product"
          style={{ height: 40, width: 40, borderRadius: "6px", objectFit: "cover" }}
        />
      ),
    },
    { field: "title", headerName: "Name", minWidth: 130, renderCell: (r) => r?.title || "-" },
    { field: "category", headerName: "Category", width: 120, renderCell: (r) => r?.categoryId?.name || "-" },
    {
      field: "description",
      headerName: "Description",
      minWidth: 180,
      cellSx: { maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      renderCell: (r) => r?.description || "-",
    },
    {
      field: "rating",
      headerName: "Review & Rating",
      width: 160,
      renderCell: (r) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StarRating rating={r?.rating || 0} />
          <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>{r?.rating || 0}</Typography>
        </Box>
      ),
    },
    { field: "price", headerName: "Price", width: 90, renderCell: (r) => formatMoney(r?.price) },
    { field: "discountedProce", headerName: "Discounted Price", width: 130, renderCell: (r) => formatMoney(r?.discountedProce) },
    { field: "status", headerName: "Status", width: 100, renderCell: (r) => <StatusChip value={r?.status} /> },
    {
      field: "__actions",
      headerName: "",
      width: 80,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleClickOpen(row),
            },
            {
              label: "Delete",
              icon: <DeleteIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => handleDeleteProduct(row?._id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <Card sx={{ padding: "20px", borderRadius: "25px", marginTop: "40px", marginBottom: "30px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <Typography sx={{ fontSize: { xs: "16px", sm: "20px" }, fontWeight: "600", color: "#362B24" }}>
            Product List
          </Typography>
          <TextField
            placeholder="Search by name"
            value={searchQuery}
            onChange={(e) => {
              if (e.target.value.trim() !== "" || e.target.value === "") {
                setSearchQuery(e.target.value);
              }
            }}
            size="small"
            sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                background: "#fff",
                fontFamily: "Heebo",
                "& fieldset": { borderColor: "#E7E7E7" },
                "&:hover fieldset": { borderColor: "#D7D7D7" },
                "&.Mui-focused fieldset": { borderColor: "#065F46" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#8B8B8B", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <AdminTable
          columns={columns}
          rows={allVenderProducts}
          loading={loading}
          rowKey="_id"
          emptyText="No products found"
        />
        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
          disabled={loading}
        />
      </Card>

      <ProductDetailModal
        open={open}
        onClose={handleClose}
        product={selectedRow}
        reviews={productDetailsData?.reviews}
      />
    </div>
  );
};

export default AllProductList;
